import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Warehouse,
  PackageSearch,
  ClipboardList,
  ArrowRightLeft,
  Cylinder,
  Activity,
  ArrowRight,
} from 'lucide-react';
import { yardApi, YardDashboard as YardDashboardData } from '@/api/services/yard.api';
import { toast } from 'sonner';

export default function YardDashboard() {
  const [dashboard, setDashboard] = useState<YardDashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const data = await yardApi.getDashboard();
      setDashboard(data);
    } catch (error) {
      console.error('Error fetching yard dashboard:', error);
      toast.error('Failed to load yard dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  const stats = dashboard?.stats;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Yard Dashboard</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Overview of yard operations and warehouse management
          </p>
        </div>
        <Link to="/yard/asset-search">
          <Button>
            <PackageSearch className="h-4 w-4 mr-2" />
            Search Assets
          </Button>
        </Link>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Warehouse className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Storage Locations</p>
                <p className="text-2xl font-semibold">{stats?.storage.totalLocations || 0}</p>
                <p className="text-xs text-slate-400">
                  {stats?.storage.byStatus.AVAILABLE || 0} available
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <ArrowRightLeft className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Putaway Tasks</p>
                <p className="text-2xl font-semibold">{stats?.putaway.pending || 0}</p>
                <p className="text-xs text-slate-400">
                  {stats?.putaway.inProgress || 0} in progress
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <ClipboardList className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Job Kits</p>
                <p className="text-2xl font-semibold">{stats?.jobKits.ready || 0}</p>
                <p className="text-xs text-slate-400">
                  {stats?.jobKits.pendingPick || 0} pending pick
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Activity className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pick Lists</p>
                <p className="text-2xl font-semibold">{stats?.picking.pending || 0}</p>
                <p className="text-xs text-slate-400">
                  {stats?.picking.completedToday || 0} completed today
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                <Cylinder className="h-6 w-6 text-slate-600 dark:text-slate-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pipe Inventory</p>
                <p className="text-2xl font-semibold">{stats?.pipeTally.totalJoints || 0}</p>
                <p className="text-xs text-slate-400">
                  {stats?.pipeTally.totalFootage?.toLocaleString() || 0} ft
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/yard/locations">
          <Card className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer">
            <CardContent className="pt-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Warehouse className="h-5 w-5 text-slate-500" />
                <span className="font-medium">Storage Locations</span>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/yard/putaway">
          <Card className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer">
            <CardContent className="pt-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ArrowRightLeft className="h-5 w-5 text-slate-500" />
                <span className="font-medium">Putaway Tasks</span>
              </div>
              <Badge variant="secondary">{stats?.putaway.pending || 0}</Badge>
            </CardContent>
          </Card>
        </Link>

        <Link to="/yard/job-kits">
          <Card className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer">
            <CardContent className="pt-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ClipboardList className="h-5 w-5 text-slate-500" />
                <span className="font-medium">Job Kits</span>
              </div>
              <Badge variant="secondary">{stats?.jobKits.ready || 0} ready</Badge>
            </CardContent>
          </Card>
        </Link>

        <Link to="/yard/pipe-tally">
          <Card className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer">
            <CardContent className="pt-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Cylinder className="h-5 w-5 text-slate-500" />
                <span className="font-medium">Pipe Tally</span>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Putaway Tasks */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ArrowRightLeft className="h-4 w-4" />
              Recent Putaway
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {dashboard?.recentActivity.putaways.length === 0 ? (
              <p className="text-sm text-slate-500">No recent putaway tasks</p>
            ) : (
              dashboard?.recentActivity.putaways.slice(0, 5).map((task) => (
                <div key={task.id} className="flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium">{task.item.name}</p>
                    <p className="text-slate-500">{task.taskNumber}</p>
                  </div>
                  <Badge
                    variant={
                      task.status === 'COMPLETED' ? 'default' :
                      task.status === 'IN_PROGRESS' ? 'secondary' : 'outline'
                    }
                  >
                    {task.status.replace('_', ' ')}
                  </Badge>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Recent Job Kits */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ClipboardList className="h-4 w-4" />
              Recent Job Kits
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {dashboard?.recentActivity.jobKits.length === 0 ? (
              <p className="text-sm text-slate-500">No recent job kits</p>
            ) : (
              dashboard?.recentActivity.jobKits.slice(0, 5).map((kit) => (
                <div key={kit.id} className="flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium">{kit.name}</p>
                    <p className="text-slate-500">{kit.kitNumber}</p>
                  </div>
                  <Badge
                    variant={
                      kit.status === 'READY' ? 'default' :
                      kit.status === 'ISSUED' ? 'secondary' :
                      kit.status === 'PICKING' ? 'outline' : 'outline'
                    }
                  >
                    {kit.status.replace('_', ' ')}
                  </Badge>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Recent Pick Lists */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Recent Pick Lists
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {dashboard?.recentActivity.pickLists.length === 0 ? (
              <p className="text-sm text-slate-500">No recent pick lists</p>
            ) : (
              dashboard?.recentActivity.pickLists.slice(0, 5).map((list) => (
                <div key={list.id} className="flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium">{list.pickListNumber}</p>
                    <p className="text-slate-500">{list.sourceType}</p>
                  </div>
                  <Badge
                    variant={
                      list.status === 'COMPLETED' ? 'default' :
                      list.status === 'IN_PROGRESS' ? 'secondary' : 'outline'
                    }
                  >
                    {list.status.replace('_', ' ')}
                  </Badge>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
